import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environment/environment';

declare let gtag: Function;

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  private readonly measurementId = 'G-3YG2Y1XQ0Y';
  private isInitialized = false;

  constructor(private router: Router) {
    // Only initialize if not in debug mode
    if (!environment.debug) {
      this.initialize();
    }
  }

  /**
   * Initialize Google Analytics
   * Sets up page view tracking and ensures gtag is available
   */
  private initialize(): void {
    if (this.isInitialized) {
      return;
    }

    // Check if gtag is already loaded (from index.html)
    if (typeof (window as any).gtag === 'undefined') {
      console.warn('[GoogleAnalytics] gtag not found. Make sure the script is loaded in index.html');
      return;
    }

    // Set up automatic page view tracking
    this.trackPageViews();
    
    this.isInitialized = true;
  }

  /**
   * Automatically track page views on route changes
   */
  private trackPageViews(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.trackPageView(event.urlAfterRedirects);
    });
  }

  /**
   * Track a page view
   * @param pagePath The path of the page to track
   */
  public trackPageView(pagePath: string): void {
    if (environment.debug) {
      return;
    }

    if (typeof (window as any).gtag === 'undefined') {
      console.warn('[GoogleAnalytics] gtag not available for page view tracking');
      return;
    }

    try {
      (window as any).gtag('config', this.measurementId, {
        page_path: pagePath
      });
    } catch (error) {
      console.error('[GoogleAnalytics] Error tracking page view:', error);
    }
  }

  /**
   * Track a custom event
   * @param eventName The name of the event
   * @param eventParams Optional parameters for the event
   */
  public trackEvent(eventName: string, eventParams?: { [key: string]: any }): void {
    if (environment.debug) {
      return;
    }

    if (typeof (window as any).gtag === 'undefined') {
      console.warn('[GoogleAnalytics] gtag not available for event tracking');
      return;
    }

    try {
      (window as any).gtag('event', eventName, eventParams || {});
    } catch (error) {
      console.error('[GoogleAnalytics] Error tracking event:', error);
    }
  }

  /**
   * Set user properties
   * @param properties User properties to set
   */
  public setUserProperties(properties: { [key: string]: any }): void {
    if (environment.debug) {
      return;
    }

    if (typeof (window as any).gtag === 'undefined') {
      console.warn('[GoogleAnalytics] gtag not available for setting user properties');
      return;
    }

    try {
      (window as any).gtag('set', 'user_properties', properties);
    } catch (error) {
      console.error('[GoogleAnalytics] Error setting user properties:', error);
    }
  }

  /**
   * Check if Google Analytics is available and initialized
   */
  public isAvailable(): boolean {
    return !environment.debug && typeof (window as any).gtag !== 'undefined' && this.isInitialized;
  }
}

